import {
    Grid,
    GridColumn,
    GridColumnMenuSort,
    GridDataStateChangeEvent,
    GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import {
    PanelBar,
    PanelBarItem,
    PanelBarItemProps,
    PanelBarUtils,
    PanelBarSelectEventArguments,
} from '@progress/kendo-react-layout'
import { setExpandedState } from '@progress/kendo-react-data-tools'

import { useCallback, useEffect, useRef, useState } from 'react'
import {
    cellCurrency,
    cellShortDate,
    cellRender,
    cellReturnOrderDetailId,
    processWithGroups,
    createAppState,
    cellSalesOrderWindow,
    cellOpenInsight,
    cellOpenNotes,
} from './AssemblySearchGridFunctions'
import { CustomPager } from './CustomPager'
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns'
import { useDashboardContext } from '../../Context/AssemblySearchResultsContext'
import style from './GridComponentsStyle.module.scss'
import './GridTheme.scss'
import { getBaseURL } from '../../../GlobalComponents/URLs'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs'
import { AWCButton, BUTTON_TYPES, FastQuoteInput } from '../../../GlobalComponents/Inputs/Inputs'

const AssemblySearchResultsGrid = () => {
    const {
        dataAvailable,
        appState,
        setAppState,
        dataState,
        setDataState,
        collapsedState,
        setCollapsedState,
        operationsResult,
        setOperationsResult,
        setCurrentPage,
    } = useDashboardContext()

    const _grid = useRef<any>()

    interface EnabledFilter {
        field: string
        value: string
        operator: string
    }

    const [filterPanelBarItems, setFilterPanelBarItems] = useState<PanelBarItem[]>([])
    const [expandedPanelBarItems, setExpandedPanelBarItems] = useState<string[]>([])
    const [lastFilterAdded, setLastFilterAdded] = useState<{
        title: string
        children: string[]
    }>()
    const [enabledFilters, setEnabledFilters] = useState<EnabledFilter[]>([])
    const [lastFilterEnabled, setLastFilterEnabled] = useState<EnabledFilter>()
    const [dataAtLastFilter, setDataAtLastFilter] = useState<AssemblySearchResult[]>()

    useEffect(() => {
        const script = document.createElement('script')
        script.src = `${getBaseURL()}scripts/base-page.min.js`
        script.async = true
        document.body.appendChild(script)

        return () => {
            document.body.removeChild(script) // Cleanup when the component unmounts
        }
    }, [])

    useEffect(() => {
        buildFilterPanel(dataAvailable)
    }, [dataAvailable])

    // Dynamically include only non-empty attribute fields in filter panels
    const buildFilterPanel = (data: AssemblySearchResult[]) => {
        if (!data || data.length === 0) return []

        // Get all possible keys from data
        const attributeKeys = Object.keys(data[0].Attributes)

        // Filter out empty attribute columns
        const nonEmptyAttributeColumns = attributeKeys.filter((key) =>
            data.some(
                (item) =>
                    item.Attributes[key] !== null &&
                    item.Attributes[key] !== undefined &&
                    item.Attributes[key] !== ''
            )
        )

        nonEmptyAttributeColumns.push('Description')
        nonEmptyAttributeColumns.push('IAM')
        nonEmptyAttributeColumns.sort()

        const filterData: PanelBarItemProps[] = nonEmptyAttributeColumns.map((col) => ({
            id: col,
            title: panelTitleMarkup(col),
            children: panelChildren(data, col),
            expanded: expandedPanelBarItems.includes(col),
        }))
        setDataAtLastFilter(data)
        setFilterPanelBarItems(PanelBarUtils.mapItemsToComponents(filterData))
    }

    const panelTitleMarkup = (col: string) => {
        return (
            <div className={enabledFilters.some((f) => f.field === col) ? style.filterActive : ''}>
                {col}
            </div>
        )
    }

    const panelChildren = (data: AssemblySearchResult[], col: string) => {
        return (
            lastFilterAdded?.title === col && col !== 'Description'
                ? lastFilterAdded.children
                : Array.from(
                      new Set(
                          data
                              .filter((item) => item.Attributes[col] !== '')
                              .map((item) =>
                                  col === 'Description' ? '' : item[col] || item.Attributes[col]
                              )
                      )
                  )
        )
            .sort()
            .map((item) => {
                return {
                    title:
                        col === 'Description' ? (
                            <div className='text-filter'>
                                <FastQuoteInput
                                    value={
                                        enabledFilters.find(
                                            (f) => f.field === col && f.value === item
                                        )?.value
                                    }
                                    onChange={(newValue: string) =>
                                        onTextFilterChange(col, newValue)
                                    }
                                    placeholder='Enter description filter here'
                                />
                            </div>
                        ) : (
                            <Checkbox
                                label={item}
                                checked={enabledFilters.some(
                                    (f) => f.field === col && f.value === item
                                )}
                                onChange={(e: CheckboxChangeEvent) =>
                                    onCheckFilterChange(e.target.value as boolean, col, item)
                                }
                            />
                        ),
                }
            })
    }

    const handleExpandChange = (event: PanelBarSelectEventArguments) => {
        const itemId = event.target.props.id as string

        setExpandedPanelBarItems((prev) =>
            prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
        )
    }

    const onTextFilterChange = (field: string, value: string) => {
        removeTextFilter(field)

        if (value !== '')
            setLastFilterEnabled({
                field: field,
                value: value,
                operator: 'contains',
            })
    }

    const onCheckFilterChange = (
        newValue: boolean,
        attributeField: string,
        attributeValue: string
    ) => {
        if (newValue)
            setLastFilterEnabled({
                field: attributeField,
                value: attributeValue,
                operator: 'eq',
            })
        else
            removeCheckedFilter({
                field: attributeField,
                value: attributeValue,
                operator: 'eq',
            })
    }

    const removeTextFilter = (field: string) => {
        setEnabledFilters((prevFilters) => prevFilters.filter((f) => f.field !== field))
    }

    const removeCheckedFilter = (newFilter: EnabledFilter) => {
        setEnabledFilters((prevFilters) =>
            prevFilters.filter((f) => f.field !== newFilter.field || f.value !== newFilter.value)
        )
    }

    useEffect(() => {
        if (!lastFilterEnabled || !dataAtLastFilter) return

        addEnabledFilter(lastFilterEnabled)

        if (
            lastFilterAdded?.title !== lastFilterEnabled.field ||
            lastFilterEnabled.field === 'Description'
        )
            setLastFilterAdded({
                title: lastFilterEnabled.field,
                children: Array.from(
                    new Set(
                        dataAtLastFilter
                            .filter((item) => item.Attributes[lastFilterEnabled.field] !== '')
                            .map(
                                (item) =>
                                    item[lastFilterEnabled.field] ||
                                    item.Attributes[lastFilterEnabled.field]
                            )
                    )
                ),
            })
    }, [lastFilterEnabled])

    // Add a filter
    const addEnabledFilter = (newFilter: EnabledFilter) => {
        setEnabledFilters((prevFilters) => [...prevFilters, newFilter])
    }

    useEffect(() => {
        const checkedAttributeNames = Array.from(new Set(enabledFilters.map((item) => item.field)))

        const newFilters: CompositeFilterDescriptor = {
            logic: 'and',
            filters: checkedAttributeNames.map((a) => {
                return {
                    logic: 'or',
                    filters: Array.from(new Set(enabledFilters.map((item) => item)))
                        .filter((b) => b.field === a)
                        .map((b) => {
                            return {
                                field:
                                    ['Description', 'IAM'].indexOf(a) > -1
                                        ? a
                                        : `Attributes['${a}']`,
                                operator: a === 'Description' ? 'contains' : 'eq',
                                value: b.value,
                            }
                        }),
                }
            }),
        }

        handleFilterChange(newFilters)
    }, [enabledFilters])

    const handleFilterChange = (newFilter: CompositeFilterDescriptor | null) => {
        const f = newFilter || {
            logic: 'and',
            filters: [],
        }

        const filteredData = f.filters.length > 0 ? filterBy(dataAvailable, f) : dataAvailable
        const newDataState = { ...dataState, filter: f }
        const groupedData = processWithGroups(filteredData, newDataState, [])
        setOperationsResult(groupedData)
        setDataState(newDataState) // Update state
        const skip = newDataState.skip ? newDataState.skip : 0
        const take = newDataState.take ? newDataState.take : 10
        setCurrentPage(skip / take + 1)
        setAppState(createAppState(skip, take, groupedData.data))
        buildFilterPanel(filteredData)
    }

    const dataStateChange = (event: GridDataStateChangeEvent) => {
        const newDataState = processWithGroups(dataAvailable, event.dataState, [])
        setOperationsResult(newDataState)
        setDataState(event.dataState)
        const skip = event.dataState.skip ? event.dataState.skip : 0
        const take = event.dataState.take ? event.dataState.take : 10
        setCurrentPage(skip / take + 1)
        setAppState(createAppState(skip, take, newDataState.data))
    }

    const pageChange = (event: GridPageChangeEvent) => {
        setAppState(createAppState(event.page.skip, event.page.take, operationsResult.data))
    }

    const handleTakeChange = (event: DropDownListChangeEvent) => {
        setCurrentPage(1)
        setAppState(createAppState(0, event.target.value, operationsResult.data))
        setCollapsedState([])
    }

    const onExpandChange = useCallback(
        (event) => {
            const item = event.dataItem
            if (item.groupId) {
                const newCollapsedIds = !event.value
                    ? [...collapsedState, item.groupId]
                    : collapsedState.filter((groupId) => groupId !== item.groupId)
                setCollapsedState(newCollapsedIds)
                const expandedData = setExpandedState({
                    data: operationsResult.data,
                    collapsedIds: newCollapsedIds,
                })
                setAppState(createAppState(appState.skip, appState.pageSize, expandedData))
            }
        },
        [appState, collapsedState]
    )

    const getColumnClass = (field: string): string | undefined => {
        if (GridColumnMenuSort.active(field, dataState.sort)) {
            return style.gActive
        } else if (GridColumnMenuSort.active(field, dataState.sort)) {
            return style.sortActive
        }

        return undefined
    }

    const calculateWidth = (field: string) => {
        let maxWidth = 0
        const headerSize = getTextWidth(field) + 27
        appState.items.forEach((item) => {
            const text = item.items[0][field] || item.items[0].Attributes[field]
            const valueSize = getTextWidth(text)
            const size = Math.max(valueSize, headerSize) + 28
            if (size > maxWidth) maxWidth = size
        })
        return maxWidth
    }

    const getTextWidth = (text: string): number => {
        const canvas: HTMLCanvasElement =
            (getTextWidth as any).canvas ||
            ((getTextWidth as any).canvas = document.createElement('canvas'))

        const context: CanvasRenderingContext2D | null = canvas.getContext('2d')
        if (!context) throw new Error('Unable to get canvas 2D context')

        context.font = 'normal 14px Roboto'
        const metrics: TextMetrics = context.measureText(text)
        return metrics.width
    }

    const clearFilters = () => {
        setEnabledFilters([])
        setLastFilterEnabled(undefined)
        setDataAtLastFilter(undefined)
        setExpandedPanelBarItems([])
    }

    return (
        <>
            <div className={`gridContainer ${style.gridContainer}`}>
                <div className={style.filterContainer}>
                    <div className={style.filterPanel}>
                        <PanelBar
                            children={filterPanelBarItems}
                            onSelect={handleExpandChange}
                            isControlled={true}
                            expanded={expandedPanelBarItems}
                        />
                    </div>
                    <div className={style.clearFilter}>
                        <div>
                            {operationsResult.total !== dataAvailable.length &&
                                `${operationsResult.total} of `}
                            {dataAvailable.length} items
                        </div>
                        <AWCButton
                            type={
                                enabledFilters.length > 0
                                    ? BUTTON_TYPES.SECONDARY
                                    : BUTTON_TYPES.DISABLED
                            }
                            onClick={clearFilters}
                        >
                            Clear Filters
                        </AWCButton>
                    </div>
                </div>
                <Grid
                    style={{
                        height: '691px',
                        width: 'calc(100% - 300px)',
                    }}
                    resizable={true}
                    groupable={{ enabled: false }}
                    reorderable={true}
                    sortable={true}
                    total={appState.total}
                    skip={appState.skip}
                    pageable={appState.pageable}
                    pageSize={appState.pageSize}
                    take={appState.pageSize}
                    onPageChange={pageChange}
                    data={appState.items}
                    onDataStateChange={dataStateChange}
                    {...dataState}
                    onExpandChange={onExpandChange}
                    expandField='expanded'
                    cellRender={cellRender}
                    pager={(props) => CustomPager(props, handleTakeChange)}
                    scrollable='scrollable'
                    ref={_grid}
                >
                    <GridColumn title='' cell={cellReturnOrderDetailId} width={60} />
                    <GridColumn
                        field='OrderNumber'
                        title='S/O #'
                        cell={cellSalesOrderWindow}
                        headerClassName={getColumnClass('SalesOrderNo')}
                        width={90}
                    />
                    <GridColumn
                        field='LineItemNumber'
                        title='L/I'
                        headerClassName={getColumnClass('LineItemNumber')}
                        width={75}
                    />
                    <GridColumn title='Insight' cell={cellOpenInsight} width={60} />
                    <GridColumn title='Notes' cell={cellOpenNotes} width={60} />
                    <GridColumn
                        field='OrderEntryDate'
                        title='Entry Date'
                        filter='date'
                        cell={cellShortDate}
                        headerClassName={getColumnClass('OrderEntryDate')}
                        width={100}
                    />
                    <GridColumn
                        field='UnitPrice'
                        title='Price'
                        filter='date'
                        cell={cellCurrency}
                        headerClassName={getColumnClass('UnitPrice')}
                        width={110}
                    />
                    <GridColumn
                        field='ShipDate'
                        title='Ship Date'
                        filter='date'
                        cell={cellShortDate}
                        headerClassName={getColumnClass('ShipDate')}
                        width={100}
                    />
                    <GridColumn
                        field='IAM'
                        title='IAM'
                        headerClassName={getColumnClass('IAM')}
                        width={calculateWidth('IAM')}
                    />
                    <GridColumn
                        field='Description'
                        title='Description'
                        headerClassName={getColumnClass('Description')}
                    />
                </Grid>
            </div>
        </>
    )
}

export default AssemblySearchResultsGrid
